/** @jsx jsx */

import { Fragment as _, useState, useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';

export const TextEntry = props => {
  const {
    id,
    message,
    messageContainer,
    messageUnderline,
    messageTextColor = '#000',
    messageTextBackgroundColor,
    messageBorderRadius,
    messageBorderColor = '#d2dff9',
    messageFontFamily = 'inherit',
    messageFontSize = 16,
    onUpdate = () => {},
    textAlign,
    placeholderText = 'Type your message here...',
  } = props;

  const maxLinesBeforeScrolling = (props.maxLinesBeforeScrolling || 4) - 1;

  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const textArea = useRef<HTMLTextAreaElement>(null);
  const textAreaProxy = useRef<HTMLDivElement>(null);

  let computedPadding = 0;

  if (textArea.current) {
    const computedStyle = getComputedStyle(textArea.current);
    computedPadding =
      parseInt(computedStyle.paddingTop, 10) +
      parseInt(computedStyle.paddingBottom, 10);
  }

  const maxHeight =
    props.maxLinesBeforeScrolling * 2 * messageFontSize + computedPadding;

  let newLineCheck = 0;
  for (let i = 0; i < message.length; i += 1) {
    if (/\n/.test(message[i])) {
      newLineCheck += 1;
    }
  }

  const textAreaHeight =
    textAreaProxy?.current?.getBoundingClientRect().height || 0;

  const shouldScroll =
    newLineCheck > maxLinesBeforeScrolling || textAreaHeight >= maxHeight;

  const textAreaProxyHTML = message.replace(/\n/g, '<br/>');
  const placeholder = !hasFocus ? placeholderText : '';

  const sharedStyles = {
    width: '100%',
    height: '100%',
    maxHeight,
    background: 'transparent',
    padding: '5px 10px' || '15px 5px 0px 5px',
    fontFamily: messageFontFamily,
    fontSize: messageFontSize,
    color: messageTextColor,
  };

  return (
    <div
      style={{
        width: '100%',
      }}
    >
      <div
        style={{
          position: 'relative',
          maxHeight: sharedStyles.maxHeight,
          ...(messageContainer && {
            height: 150,
            padding: `10px ${messageBorderRadius || 20}px 10px 10px`,
            background: messageTextBackgroundColor,
            borderRadius: messageBorderRadius,
            boxShadow: `0 0 0 ${hasFocus ? 3 : 1}px ${
              hasFocus && messageBorderColor === 'transparent'
                ? '#d2dff9'
                : messageBorderColor
            }`,
            transition: 'box-shadow 0.1s',
          }),
        }}
      >
        {messageUnderline && (
          <div
            ref={textAreaProxy}
            style={{
              ...sharedStyles,
              visibility: 'hidden',
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
            }}
            dangerouslySetInnerHTML={{
              __html: `${textAreaProxyHTML}<br/>${
                newLineCheck > maxLinesBeforeScrolling ? ' ' : ''
              }`,
            }}
            aria-hidden="true"
          />
        )}

        <label htmlFor={id} className="sr-only">
          {placeholder}
        </label>

        <textarea
          ref={textArea}
          id={id}
          placeholder={placeholder}
          css={css({
            position: messageUnderline ? 'absolute' : 'relative',
            left: 0,
            top: 0,
            border: 'none',
            outline: 'none',
            resize: 'none',

            ...sharedStyles,
            overflow: shouldScroll ? 'auto' : 'hidden',

            textAlign: textAlign || (message || hasFocus ? 'left' : 'center'),

            ':focus': {
              border: 'none !important',
              outline: 'none !important',
            },

            '::placeholder': {
              color: messageTextColor,
            },
          })}
          onFocus={() => {
            setHasFocus(true);
          }}
          onBlur={() => {
            setHasFocus(false);
          }}
          onChange={e => {
            onUpdate(e.currentTarget.value);
          }}
          // aria-label={placeholder}
          autoComplete="off"
        />
      </div>

      {messageUnderline && (
        <div
          style={{
            width: '100%',
            height: 1,
            marginTop: 10,
            boxShadow: `0 0 0 ${hasFocus ? 1 : 0}px ${messageBorderColor}`,
            background: messageBorderColor,
            transition: 'box-shadow 0.3s',
          }}
        />
      )}
    </div>
  );
};
